import { Button, Segmented, Space, Typography } from "antd";
import L from "leaflet";
import { useEffect, useMemo } from "react";
import { MapContainer, Marker, Polygon, TileLayer, useMap, useMapEvents } from "react-leaflet";
import type { TerritoryCoordinates } from "@/modules/territories/services/territory.service";
import type { Coordinate, PlantingGeometry } from "../types";

type DrawMode = "point" | "polygon";

interface GeometryPickerProps {
	value?: PlantingGeometry;
	onChange?: (value: PlantingGeometry) => void;
	mode?: DrawMode;
	onModeChange?: (mode: DrawMode) => void;
	/** Lock the draw mode switch (mode dictated by plant type) */
	modeDisabled?: boolean;
	/** Read-only reference polygon drawn underneath (e.g. the target territory boundary) */
	boundary?: { coordinates: Coordinate[][] } | TerritoryCoordinates | null;
}

/** Convert GeoJSON [lng, lat] to Leaflet [lat, lng] */
const toLatLng = ([lng, lat]: Coordinate): [number, number] => [lat, lng];

function FitToValue({ positions }: { positions: [number, number][] }) {
	const map = useMap();
	useEffect(() => {
		if (positions.length === 1) {
			map.setView(positions[0], 15);
		} else if (positions.length > 1) {
			map.fitBounds(L.latLngBounds(positions), { padding: [30, 30], maxZoom: 16 });
		}
	}, [map, positions]);
	return null;
}

function InvalidateSize() {
	const map = useMap();
	useEffect(() => {
		// Leaflet mis-measures its container when the map mounts inside a modal
		// that is still animating open, leaving tiles partially rendered.
		// Re-measure right after paint and again after the modal transition.
		const timers = [
			setTimeout(() => map.invalidateSize(), 0),
			setTimeout(() => map.invalidateSize(), 300),
		];
		return () => {
			for (const t of timers) {
				clearTimeout(t);
			}
		};
	}, [map]);
	return null;
}

function MapClickHandler({
	mode,
	value,
	onChange,
}: {
	mode: DrawMode;
	value?: PlantingGeometry;
	onChange?: (value: PlantingGeometry) => void;
}) {
	useMapEvents({
		click(e) {
			const coord: Coordinate = [e.latlng.lng, e.latlng.lat];
			if (mode === "point") {
				onChange?.({ type: "Point", coordinates: coord });
				return;
			}
			// polygon: append vertex to the (single) outer ring
			const existing =
				value && value.type === "Polygon" ? (value.coordinates[0]?.slice() ?? []) : [];
			onChange?.({ type: "Polygon", coordinates: [[...existing, coord]] });
		},
	});
	return null;
}

export function GeometryPicker({
	value,
	onChange,
	mode,
	onModeChange,
	modeDisabled,
	boundary,
}: GeometryPickerProps) {
	const internalMode: DrawMode = mode ?? "point";

	const boundaryPositions: [number, number][] = useMemo(() => {
		if (!boundary?.coordinates?.length) {
			return [];
		}
		// MultiPolygon coordinates: [number, number][][][]
		if (Array.isArray(boundary.coordinates[0]?.[0]?.[0])) {
			const coords = boundary.coordinates as [number, number][][][];
			const firstRing = coords[0]?.[0] ?? [];
			return firstRing.map(([lng, lat]) => [lat, lng]);
		}
		const ring = (boundary.coordinates as [number, number][][])[0] ?? [];
		return ring.map(([lng, lat]) => [lat, lng]);
	}, [boundary]);

	const markerPositions: [number, number][] = useMemo(() => {
		if (!value) {
			return [];
		}
		if (value.type === "Point") {
			return [toLatLng(value.coordinates)];
		}
		if (value.type === "MultiPoint") {
			return value.coordinates.map(toLatLng);
		}
		return [];
	}, [value]);

	const polygonPositions: [number, number][] = useMemo(() => {
		if (!value) {
			return [];
		}
		if (value.type === "Polygon") {
			return (value.coordinates[0] ?? []).map(toLatLng);
		}
		if (value.type === "MultiPolygon") {
			return (value.coordinates[0]?.[0] ?? []).map(toLatLng);
		}
		return [];
	}, [value]);

	const allPositions = useMemo(
		() =>
			markerPositions.length || polygonPositions.length
				? [...markerPositions, ...polygonPositions]
				: boundaryPositions,
		[markerPositions, polygonPositions, boundaryPositions]
	);

	const clear = () => {
		// Clear the controlled value; the form validator treats undefined as "required"
		onChange?.(undefined as unknown as PlantingGeometry);
	};

	return (
		<div className="space-y-2">
			<Space wrap>
				<Segmented<DrawMode>
					value={internalMode}
					onChange={(v) => onModeChange?.(v)}
					disabled={modeDisabled}
					options={[
						{ label: "Nuqta", value: "point" },
						{ label: "Poligon", value: "polygon" },
					]}
				/>
				<Button size="small" onClick={clear} disabled={!value}>
					Tozalash
				</Button>
				<Typography.Text type="secondary" className="text-xs">
					{internalMode === "point"
						? "Nuqta qo'yish uchun xaritani bosing"
						: "Poligon uchun cho'qqilarni ketma-ket bosing"}
					{boundaryPositions.length > 0 && " (kesik chiziq — tanlangan hudud chegarasi)"}
				</Typography.Text>
			</Space>
			<div className="h-72 w-full overflow-hidden rounded-lg border border-neutral-200">
				<MapContainer center={[41.56, 60.62]} zoom={7} className="h-full w-full" scrollWheelZoom>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>
					<InvalidateSize />
					<MapClickHandler mode={internalMode} value={value} onChange={onChange} />
					{allPositions.length > 0 && <FitToValue positions={allPositions} />}
					{boundaryPositions.length > 0 && (
						<Polygon
							positions={boundaryPositions}
							pathOptions={{
								color: "#64748b",
								dashArray: "6 4",
								fillOpacity: 0.05,
								weight: 1.5,
							}}
							interactive={false}
						/>
					)}
					{markerPositions.map((pos) => (
						<Marker key={`${pos[0]},${pos[1]}`} position={pos} />
					))}
					{polygonPositions.length > 0 && (
						<Polygon
							positions={polygonPositions}
							pathOptions={{ color: "#22c55e", fillOpacity: 0.3 }}
						/>
					)}
				</MapContainer>
			</div>
		</div>
	);
}
